"use client";

// Drawn inline (not an image file) so it is always there the moment the popup opens.
function CloseIcon() {
  return (
    <svg
      viewBox="0 0 46 46"
      aria-hidden="true"
      className="block h-[46px] w-[46px] mobile:h-[33px] mobile:w-[33px]"
    >
      <circle cx="23" cy="23" r="20.5" fill="none" stroke="#6D778E" strokeWidth="3.5" />
      <path d="M16 16 L30 30 M30 16 L16 30" stroke="#6D778E" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

// Shell shared by every step of the "add message" popup. Same look as the existing popups
// (light outer box, close icon), with the darker bordered panel the new designs add.
export function PopupShell({
  title,
  titleClassName = "",
  subtitle,
  onClose,
  children,
  footer,
  panelTitle,
  // The order popups put the title inside the dark panel instead of above it.
  titleInside = false,
  // compact: the narrower popup of the design (522px, 40px margins); flat: no inner panel (the content sits on the
  // popup itself); closeOnBackdrop={false}: a click outside does not close it (forms: typing must never be lost).
  compact = false,
  flat = false,
  // flatOnPhone: the panel is there on a tablet / desktop, but on a phone the content sits on the popup itself
  flatOnPhone = false,
  closeOnBackdrop = true,
  // titleBelowClose: the close icon has a row of its own and the title starts under it (the empty space lets the popup breathe)
  titleBelowClose = false,
  // tablet / desktop only (never the phone): `figmaDesktop` gives the popup the proportions of the desktop design
  // (taller: 20px above the close icon, 40px below the last row); `panelPadding` replaces the padding of the grey panel
  figmaDesktop = false,
  panelPadding = "",
  // tablet / desktop only: the class that sets the distance between the header and the grey panel
  // (a whole class name, so that Tailwind finds it), e.g. "[@media(min-width:744px)]:!mt-[28px]"
  panelTopClass = "",
}) {
  const headerTitle = titleInside || titleBelowClose ? null : title;
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-[#00000070] px-[7px] pb-10 pt-[110px] mobile:pt-[88px]"
      onMouseDown={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={panelTitle || title}
        className={`flex w-full flex-col rounded-[24px] bg-[#E8F0F6] mobile:max-w-[346px] mobile:p-[13px] ${compact ? "max-w-[522px] p-[40px]" : "max-w-[600px] p-[28px]"} ${figmaDesktop ? "[@media(min-width:744px)]:pb-[40px] [@media(min-width:744px)]:pt-[20px]" : ""}`}
      >
        <div className="flex items-start justify-between">
          <div className={`pr-3 pt-[6px] ${titleClassName}`}>
            {headerTitle && (
              <h2 className="font-variation-customOpt20 text-[24px] leading-[28px] text-[#414B5A] mobile:text-[14px] mobile:leading-[18px]">
                {headerTitle}
              </h2>
            )}
            {subtitle && (
              <p className="mt-2 text-[16px] leading-[20px] text-[#0A85C2] mobile:text-[12px] mobile:leading-[16px]">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zapri"
            className="relative z-10 ml-auto shrink-0 cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {titleBelowClose && title && (
          <h2 className="mt-1 px-2 font-variation-customOpt20 text-[24px] leading-[28px] text-[#414B5A] [@media(min-width:744px)]:pl-[20px] [@media(min-width:744px)]:pt-[8px] mobile:px-1 mobile:text-[14px] mobile:leading-[18px]">
            {title}
          </h2>
        )}

        <div
          className={`${flat ? "px-2 pb-1 mobile:px-1" : flatOnPhone ? `rounded-[20px] border border-[#6D778E]/70 bg-[#E1E6EC] mobile:rounded-none mobile:border-0 mobile:bg-transparent mobile:px-1 mobile:py-0 ${panelPadding || (compact ? "px-7 py-6" : "px-8 py-8")}` : `rounded-[20px] border border-[#6D778E]/70 bg-[#E1E6EC] mobile:px-6 mobile:py-7 ${panelPadding || (compact ? "px-7 py-6" : "px-8 py-8")}`} ${
            `${panelTopClass} ` + (headerTitle ? `mt-5 mobile:mt-3 ${titleClassName.includes("hidden") ? "desktop:mt-1 tablet:mt-1" : ""}` : titleBelowClose ? "mt-4 mobile:mt-3 [@media(min-width:744px)]:mt-[24px]" : "mt-1")
          }`}
        >
          {titleInside && title && (
            <h2 className="mb-8 text-[24px] leading-[28px] text-[#1E2125] mobile:mb-6 mobile:text-[20px] mobile:leading-[24px]">
              {title}
            </h2>
          )}
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
}

// Smaller, lighter button for the "back" / "preview" actions next to the main blue-bordered one.
export function SecondaryButton({ children, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-[60px] rounded-[10px] border border-[#B9C6DD] bg-[#E7EBF0] px-6 text-[20px] text-[#7C879A] shadow-custom-dark-bottom ${className}`}
    >
      {children}
    </button>
  );
}

// Lines aligned to the left or centred.
export function AlignIcon({ align }) {
  const lines = align === "left" ? [14, 10, 14, 8] : [14, 8, 14, 10];
  return (
    <svg width="24" height="20" viewBox="0 0 24 20" aria-hidden="true">
      {lines.map((w, i) => (
        <rect
          key={i}
          x={align === "left" ? 3 : (24 - w) / 2}
          y={3 + i * 4}
          width={w}
          height="2"
          rx="1"
          fill="#6D778E"
        />
      ))}
    </svg>
  );
}

// One option of an either/or choice. The selected one gets a blue border and a red check mark.
export function ChoiceOption({ selected, onSelect, label, children, swatch }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className={`relative flex h-[48px] w-[48px] items-center justify-center rounded-[6px] mobile:h-[48px] mobile:w-[48px] ${
        swatch ? "" : "bg-[#F2F8FF66] shadow-custom-dark-to-white"
      } ${selected ? "border border-[#105CCF]" : "border border-transparent"}`}
      style={swatch ? { background: swatch } : undefined}
    >
      {children}
      {selected && (
        <span
          aria-hidden="true"
          className="absolute bottom-[2px] right-[4px] text-[14px] font-bold leading-none text-[#EB1D1D]"
        >
          ✓
        </span>
      )}
    </button>
  );
}
